/**
 * --------------------------------------------------------------------------
 * External Popover Web Component
 * --------------------------------------------------------------------------
 * This component handles fetching and displaying content from a remote URL
 * in a popover. It includes a sophisticated caching system for Shopify
 * template IDs to optimize performance by pre-caching on hover.
 */
if (!customElements.get('external-popover')) {
    class ExternalPopover extends HTMLElement {
        static CONSTANTS = {
            TEMPLATE_ID_PREFIX: 'templateId_',
            FETCHING_PREFIX: 'fetching_',
            PAGE_CONTENT_PREFIX: 'pageContent_',
            EVENT_PREFIX: 'template',
            EVENT_SUFFIX: 'set',
            MAX_RETRIES: 3,
        };

        // In-memory fallback storage when localStorage quota is exceeded
        static memoryStorage = new Map();

        constructor() {
            super();
            this.button = null;
            this.popoverElement = null;
            this.isLoaded = false;
            this.pendingClickResolve = null;

            // Component Configuration
            // Use getter to read from dataset.url dynamically (allows updates after initialization)
            // this.url is now a getter that reads from dataset.url at access time
            this.popoverTarget = this.dataset.popoverTarget;
            this.objectType = this.dataset.objectType;
            this.templateSuffix = this.dataset.templateSuffix;
            this.cacheKey = null;
            this.specificEventName = null;
        }

        /**
         * Getter for URL that reads from dataset.url dynamically.
         * This allows the URL to be updated after component initialization.
         * @returns {string} The current URL from data-url attribute
         */
        get url() {
            return this.dataset.url;
        }

        /**
         * Setter for URL that updates both dataset.url and the internal property.
         * @param {string} value - The new URL value
         */
        set url(value) {
            this.dataset.url = value;
        }

        connectedCallback() {
            if (this.initialize()) {
                this.addEventListeners();
            }
        }

        disconnectedCallback() {
            this.removeEventListeners();
        }

        // --------------------------------------------------------------------
        // SETUP & INITIALIZATION
        // --------------------------------------------------------------------

        /**
         * Initializes the component, validates required data attributes,
         * and sets up initial properties.
         * @returns {boolean} True if initialization is successful, false otherwise.
         */
        initialize() {
            this.button = this.querySelector('button');
            
            // Validate required data attributes
            // Note: this.url is now a getter that reads from dataset.url dynamically
            if (!this.url || !this.popoverTarget || !this.objectType) {
                console.error('ExternalPopover Error: Missing required data attributes (data-url, data-popover-target, data-object-type).', this);
                return false;
            }

            if (!this.button) {
                console.error('ExternalPopover Error: Could not find a <button> element inside the component.', this);
                return false;
            }
            
            // Find popover element - either by popovertarget attribute or by data-popover-modal attribute
            let popoverElementId = this.button.getAttribute('popovertarget');
            
            if (popoverElementId) {
                this.popoverElement = document.getElementById(popoverElementId);
            } else {
                // If no popovertarget, find popover by data attribute
                this.popoverElement = this.querySelector('[data-popover-modal="true"]');
                
                // Determine popover ID: use modal_handle (popoverTarget) if provided, otherwise generate UUID
                if (this.popoverElement && !this.popoverElement.id) {
                    // Use modal_handle from data-popover-target if provided and not blank
                    if (this.popoverTarget && this.popoverTarget.trim() !== '') {
                        popoverElementId = this.popoverTarget.trim();
                    } else {
                        // Generate UUID using crypto.randomUUID() (available in modern browsers)
                        // Fallback to a simple random string if not available
                        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                            popoverElementId = crypto.randomUUID();
                        } else {
                            // Fallback: simple random string (not cryptographically secure but good enough for IDs)
                            popoverElementId = 'popover-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                        }
                    }
                    
                    this.popoverElement.id = popoverElementId;
                    this.button.setAttribute('popovertarget', popoverElementId);
                } else if (this.popoverElement && this.popoverElement.id) {
                    // Popover already has an ID, use it
                    popoverElementId = this.popoverElement.id;
                    this.button.setAttribute('popovertarget', popoverElementId);
                }
            }

            if (!this.popoverElement) {
                console.error(`ExternalPopover Error: Could not find the popover element.`, this);
                return false;
            }
            
            // Generate cache key and event name
            this.cacheKey = this.templateSuffix ? `${this.objectType}-${this.templateSuffix}` : this.objectType;
            this.specificEventName = `${ExternalPopover.CONSTANTS.EVENT_PREFIX}:${this.cacheKey}:${ExternalPopover.CONSTANTS.EVENT_SUFFIX}`;
            
            return true;
        }

        /**
         * Adds all necessary event listeners.
         */
        addEventListeners() {
            this.handleClick = this.handleClick.bind(this);
            this.handleMouseEnter = this.handleMouseEnter.bind(this);
            this.handleTemplateIdSet = this.handleTemplateIdSet.bind(this);
            this.handleCloseEvent = this.handleCloseEvent.bind(this);
            
            this.button.addEventListener('click', this.handleClick);
            this.addEventListener('mouseenter', this.handleMouseEnter);
            document.addEventListener(this.specificEventName, this.handleTemplateIdSet);
            document.addEventListener('dialog-popover:close', this.handleCloseEvent);
        }
        
        /**
         * Cleans up all event listeners.
         */
        removeEventListeners() {
            if (this.button) {
                this.button.removeEventListener('click', this.handleClick);
                this.removeEventListener('mouseenter', this.handleMouseEnter);
            }
            if (this.specificEventName) {
                document.removeEventListener(this.specificEventName, this.handleTemplateIdSet);
            }
            document.removeEventListener('dialog-popover:close', this.handleCloseEvent);
        }

        // --------------------------------------------------------------------
        // EVENT HANDLERS
        // --------------------------------------------------------------------

        /**
         * Handles the click event on the button to open the popover.
         * @param {Event} event
         */
        async handleClick(event) {
            event.preventDefault();

            if (this.isLoaded) {
                await this.togglePopover();
                return;
            }

            try {
                await this.fetchAndInjectContent();
            } catch (error) {
                console.error('ExternalPopover Error: Failed to load popover content after all retries.', { component: this, error });
            }
        }
        
        /**
         * Handles the mouse enter event to pre-fetch the template ID.
         */
        async handleMouseEnter() {
            // Don't prefetch if we already have the ID or are fetching it
            if (this.getTemplateId() || this.isFetchingTemplateId()) {
                return;
            }
            await this.fetchAndCacheTemplateId();
        }
        
        /**
         * Handles the custom event fired when a template ID has been set.
         * @param {CustomEvent} event
         */
        handleTemplateIdSet(event) {
            const { templateId } = event.detail;
            if (this.pendingClickResolve) {
                this.pendingClickResolve(templateId);
                this.pendingClickResolve = null;
            }
        }
        
        /**
         * Handles the centralized close event for modals/popovers.
         * Closes this popover if it matches the modalId or if closeAll is true.
         * Works with both native popover API and fallback popover-element.
         * @param {CustomEvent} event
         */
        handleCloseEvent(event) {
            const { modalId, closeAll = false, force = false } = event.detail;

            if (!this.popoverElement) return;

            // Check if this popover should be closed
            const shouldClose = closeAll ||
                               (modalId && this.popoverElement.id === modalId) ||
                               (modalId && this.id === modalId);

            if (shouldClose && this.isPopoverOpen()) {
                this.closePopover();
            }
        }

        // --------------------------------------------------------------------
        // CACHING LOGIC
        // --------------------------------------------------------------------

        /**
         * Safely sets an item in localStorage with fallback to in-memory storage.
         * @param {string} key The storage key
         * @param {string} value The value to store
         * @returns {boolean} True if successful, false otherwise
         */
        safeSetItem(key, value) {
            try {
                localStorage.setItem(key, value);
                return true;
            } catch (error) {
                if (error.name === 'QuotaExceededError') {
                    console.warn('ExternalPopover Warning: localStorage quota exceeded. Attempting cleanup and using in-memory fallback.', { component: this });
                    // Try to clean up old entries
                    this.cleanupOldCacheEntries();
                    try {
                        // Retry after cleanup
                        localStorage.setItem(key, value);
                        return true;
                    } catch (retryError) {
                        // Still failed, use in-memory storage
                        ExternalPopover.memoryStorage.set(key, value);
                        return true;
                    }
                } else {
                    // Other errors (e.g., localStorage disabled)
                    ExternalPopover.memoryStorage.set(key, value);
                    return true;
                }
            }
        }

        /**
         * Safely gets an item from localStorage with fallback to in-memory storage.
         * @param {string} key The storage key
         * @returns {string|null} The stored value or null
         */
        safeGetItem(key) {
            try {
                return localStorage.getItem(key);
            } catch (error) {
                // Fallback to in-memory storage
                return ExternalPopover.memoryStorage.get(key) || null;
            }
        }

        /**
         * Safely removes an item from both localStorage and in-memory storage.
         * @param {string} key The storage key
         */
        safeRemoveItem(key) {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                // Ignore errors when removing
            }
            ExternalPopover.memoryStorage.delete(key);
        }

        /**
         * Cleans up old cache entries to free up localStorage space.
         * Removes entries that match the template ID pattern and are older than 24 hours.
         */
        cleanupOldCacheEntries() {
            try {
                const keysToRemove = [];
                const now = Date.now();
                const maxAge = 24 * 60 * 60 * 1000; // 24 hours
                
                // Iterate through localStorage to find old template ID entries
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(ExternalPopover.CONSTANTS.TEMPLATE_ID_PREFIX)) {
                        // Check if this is an old entry (we'll remove entries that aren't for the current cache key)
                        // For now, we'll be conservative and only remove entries that are clearly not in use
                        // A more sophisticated approach could track last access time
                        keysToRemove.push(key);
                    } else if (key && (key.startsWith(ExternalPopover.CONSTANTS.FETCHING_PREFIX) || 
                                      key.startsWith(ExternalPopover.CONSTANTS.PAGE_CONTENT_PREFIX))) {
                        // Remove fetching flags and old page content
                        keysToRemove.push(key);
                    }
                }
                
                // Remove old entries (but keep the current one)
                keysToRemove.forEach(key => {
                    if (key !== `${ExternalPopover.CONSTANTS.TEMPLATE_ID_PREFIX}${this.cacheKey}`) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (error) {
                // Ignore cleanup errors
            }
        }

        getTemplateId() {
            return this.safeGetItem(`${ExternalPopover.CONSTANTS.TEMPLATE_ID_PREFIX}${this.cacheKey}`);
        }
        
        isFetchingTemplateId() {
            return this.safeGetItem(`${ExternalPopover.CONSTANTS.FETCHING_PREFIX}${this.cacheKey}`) === 'true';
        }
        
        setTemplateId(templateId) {
            const key = `${ExternalPopover.CONSTANTS.TEMPLATE_ID_PREFIX}${this.cacheKey}`;
            this.safeSetItem(key, templateId);
            const event = new CustomEvent(this.specificEventName, {
                detail: { cacheKey: this.cacheKey, templateId }
            });
            document.dispatchEvent(event);
        }
        
        setFetchingState(isFetching) {
            const key = `${ExternalPopover.CONSTANTS.FETCHING_PREFIX}${this.cacheKey}`;
            if (isFetching) {
                this.safeSetItem(key, 'true');
            } else {
                this.safeRemoveItem(key);
            }
        }
        
        clearTemplateCache() {
            this.safeRemoveItem(`${ExternalPopover.CONSTANTS.TEMPLATE_ID_PREFIX}${this.cacheKey}`);
            this.safeRemoveItem(`${ExternalPopover.CONSTANTS.FETCHING_PREFIX}${this.cacheKey}`);
        }

        // --------------------------------------------------------------------
        // FETCHING & DATA HANDLING
        // --------------------------------------------------------------------

        /**
         * Fetches the full page content to find and cache the template ID.
         * @returns {Promise<string|null>} The found template ID or null.
         */
        async fetchAndCacheTemplateId() {
            this.setFetchingState(true);
            try {
                const response = await fetch(this.url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                
                const html = await response.text();
                const templateId = this.extractTemplateId(html);
                
                if (templateId) {
                    this.setTemplateId(templateId);
                    return templateId;
                }
            } catch (error) {
                console.error('ExternalPopover Error: Could not fetch and cache template ID.', { component: this, error });
            } finally {
                this.setFetchingState(false);
            }
            return null;
        }

        /**
         * Extracts a Shopify template ID from a string of HTML.
         * @param {string} html The HTML content to search.
         * @returns {string|null} The extracted template ID.
         */
        extractTemplateId(html) {
            const regex = /shopify-section-template--(\d+)__/;
            const match = html.match(regex);
            if (!match) {
                // Try alternative patterns in case the structure has changed
                const altRegex1 = /template--(\d+)__/;
                const altMatch1 = html.match(altRegex1);
                if (altMatch1) {
                    return altMatch1[1];
                }
                
                const altRegex2 = /"template_id":\s*(\d+)/;
                const altMatch2 = html.match(altRegex2);
                if (altMatch2) {
                    return altMatch2[1];
                }
            }
            return match ? match[1] : null;
        }

        /**
         * Extracts section HTML from a full page HTML by finding the element
         * with the matching section ID pattern.
         * @param {string} html The full page HTML content.
         * @param {string} sectionKey The section key to find (e.g., "template--123__section-name").
         * @returns {string|null} The HTML content of the matching section element.
         */
        extractSectionFromHTML(html, sectionKey) {
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;
            
            // Find the element with the section ID pattern
            // Shopify sections have IDs like: shopify-section-template--123__section-name
            const sectionId = `shopify-section-${sectionKey}`;
            const sectionElement = tempContainer.querySelector(`#${sectionId}`);
            
            if (sectionElement) {
                return sectionElement.outerHTML;
            }
            
            // Fallback: try to find by attribute or class pattern
            const sectionElements = tempContainer.querySelectorAll(`[id*="${sectionKey}"]`);
            if (sectionElements.length > 0) {
                return sectionElements[0].outerHTML;
            }
            
            return null;
        }

        /**
         * Fetches the popover content, with retry logic for stale template IDs.
         * @param {number} retryCount The current retry attempt number.
         */
        async fetchAndInjectContent(retryCount = 0) {
            const templateId = await this.waitForTemplateId();
            if (!templateId) {
                throw new Error('Could not obtain template ID.');
            }

            const sectionKey = `template--${templateId}__${this.popoverTarget}`;
            
            try {
                // Check if URL already has query parameters
                const separator = this.url.includes('?') ? '&' : '?';
                const fetchUrl = `${this.url}${separator}sections=${sectionKey}`;
                const response = await fetch(fetchUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                
                // Get response as text first (can be used for both JSON and HTML parsing)
                const responseText = await response.text();
                let html = null;
                
                // Check content-type to determine parsing strategy
                const contentType = response.headers.get('content-type');
                const isJson = contentType && contentType.includes('application/json');
                
                if (isJson) {
                    try {
                        // Try to parse as JSON (Shopify sections API format)
                        const data = JSON.parse(responseText);
                        html = data[sectionKey];
                        if (!html) {
                            throw new Error(`Section returned null for key: ${sectionKey}`);
                        }
                    } catch (jsonError) {
                        // JSON parsing failed, fall back to HTML parsing
                        console.warn('ExternalPopover Warning: Response was marked as JSON but parsing failed. Falling back to HTML parsing.', { component: this, error: jsonError.message });
                        html = this.extractSectionFromHTML(responseText, sectionKey);
                        if (!html) {
                            throw new Error(`Could not extract section from HTML for key: ${sectionKey}`);
                        }
                    }
                } else {
                    // Response is not JSON, parse as HTML
                    html = this.extractSectionFromHTML(responseText, sectionKey);
                    if (!html) {
                        throw new Error(`Could not extract section from HTML for key: ${sectionKey}`);
                    }
                }
                
                this.injectPopoverContent(html);
                this.isLoaded = true;
                this.openPopover();
                
            } catch (error) {
                if (retryCount < ExternalPopover.CONSTANTS.MAX_RETRIES && (error.message.includes('HTTP error') || error.message.includes('Section returned null') || error.message.includes('Could not extract section'))) {
                    console.warn(`ExternalPopover Warning: Section fetch failed (attempt ${retryCount + 1}/${ExternalPopover.CONSTANTS.MAX_RETRIES}), template ID may be stale. Clearing cache and fetching new template ID...`, { component: this, error: error.message });
                    
                    // Clear the stale template ID from cache
                    this.clearTemplateCache();
                    
                    // Fetch a fresh template ID and wait for it to be available
                    const newTemplateId = await this.fetchAndCacheTemplateId();
                    
                    if (!newTemplateId) {
                        throw new Error('Could not fetch new template ID after clearing stale cache.');
                    }
                    
                    // Verify the new template ID is different from the stale one
                    if (newTemplateId === templateId) {
                        console.warn(`ExternalPopover Warning: New template ID matches stale ID. This may indicate the template ID extraction is incorrect.`, { component: this, templateId: newTemplateId });
                    }
                    
                    // Wait a brief moment to ensure the cache is fully updated
                    // Then retry with the fresh template ID
                    return this.fetchAndInjectContent(retryCount + 1);
                } else {
                    if (retryCount >= ExternalPopover.CONSTANTS.MAX_RETRIES) {
                        console.error(`ExternalPopover Error: Max retry attempts exceeded. This may indicate an incorrect data-popover-target value: "${this.popoverTarget}"`, { component: this });
                    }
                    throw error;
                }
            }
        }

        /**
         * Waits for a template ID to become available, either from cache or by fetching.
         * @returns {Promise<string>} A promise that resolves with the template ID.
         */
        async waitForTemplateId() {
            const existingId = this.getTemplateId();
            if (existingId) return existingId;

            if (this.isFetchingTemplateId()) {
                return new Promise(resolve => { this.pendingClickResolve = resolve; });
            }

            return await this.fetchAndCacheTemplateId();
        }

        // --------------------------------------------------------------------
        // DOM MANIPULATION
        // --------------------------------------------------------------------

        /**
         * Injects the fetched HTML content into the popover element.
         * Supports both native popover API and fallback popover-element custom element.
         * @param {string} html The HTML string to inject.
         */
        injectPopoverContent(html) {
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;

            // Look for both native popover and fallback popover-element
            const sourcePopover = tempContainer.querySelector('[popover]') ||
                                  tempContainer.querySelector('popover-element');

            if (sourcePopover && this.popoverElement) {
                // Store the original ID to preserve references
                const originalId = this.popoverElement.id;
                const parent = this.popoverElement.parentNode;

                // Determine if we're using the fallback popover-element
                const isPopoverElement = sourcePopover.tagName.toLowerCase() === 'popover-element' ||
                                         this.popoverElement.tagName.toLowerCase() === 'popover-element';

                // If parent exists, replace the element (preferred method)
                if (parent) {
                    // Create a new element with the same tag as the TARGET element
                    // (keep the existing element type - don't change from popover-element to div)
                    const newPopoverElement = document.createElement(this.popoverElement.tagName.toLowerCase());

                    // Copy all attributes from the source element
                    Array.from(sourcePopover.attributes).forEach(attr => {
                        newPopoverElement.setAttribute(attr.name, attr.value);
                    });

                    // Ensure the ID is preserved
                    newPopoverElement.id = originalId;

                    // Copy the innerHTML
                    newPopoverElement.innerHTML = sourcePopover.innerHTML;

                    // For popover-element fallback mode, ensure it starts hidden
                    // The connectedCallback will set up the proper class-based visibility
                    if (isPopoverElement) {
                        // Pre-set the hidden state so there's no flash of content
                        // connectedCallback will add .popover-initialized class
                        newPopoverElement.style.display = 'none';
                    }

                    // Replace the old element with the new one
                    parent.replaceChild(newPopoverElement, this.popoverElement);

                    // Update the reference to point to the new element
                    this.popoverElement = newPopoverElement;
                } else {
                    // If no parent (element not in DOM), update existing element in place
                    // Copy all attributes from the source element
                    Array.from(sourcePopover.attributes).forEach(attr => {
                        if (attr.name !== 'id') { // Don't overwrite the ID
                            this.popoverElement.setAttribute(attr.name, attr.value);
                        }
                    });

                    // Update the innerHTML
                    this.popoverElement.innerHTML = sourcePopover.innerHTML;
                }

                // Ensure popover attribute is set for native popover mode
                if (!isPopoverElement && !this.popoverElement.hasAttribute('popover')) {
                    this.popoverElement.setAttribute('popover', '');
                }

                this.updatePopoverTargets();

                // Custom elements (like popover-close, popover-element) will automatically upgrade
                // when added to the DOM and their connectedCallback() will handle initialization
                // No need for manual initialization - the custom element handles it
            } else {
                console.error('ExternalPopover Error: No [popover] or popover-element found in fetched content.', { fetchedHTML: html });
            }
        }

        /**
         * Updates any popovertarget attributes in the newly injected content
         * to ensure they correctly target the main popover.
         */
        updatePopoverTargets() {
            const buttonsWithTargets = this.popoverElement.querySelectorAll('[popovertarget]');
            buttonsWithTargets.forEach(button => {
                button.setAttribute('popovertarget', this.popoverElement.id);
            });
        }

        /**
         * Checks if this is using the fallback popover-element custom element.
         * @returns {boolean}
         */
        isUsingFallbackMode() {
            return this.popoverElement &&
                   this.popoverElement.tagName.toLowerCase() === 'popover-element';
        }

        /**
         * Checks if the popover is currently open.
         * Works with both native popover API and fallback popover-element.
         * @returns {boolean}
         */
        isPopoverOpen() {
            if (!this.popoverElement) return false;

            if (this.isUsingFallbackMode()) {
                // For popover-element, check _isOpen property or .popover-open class
                return this.popoverElement._isOpen === true ||
                       this.popoverElement.classList.contains('popover-open');
            }
            // For native popover, use matches
            return this.popoverElement.matches(':popover-open');
        }

        /**
         * Ensures the popover-element is fully upgraded before using it.
         * @returns {Promise<void>}
         */
        async ensurePopoverElementReady() {
            if (!this.isUsingFallbackMode()) return;

            // Wait for the custom element to be defined
            await customElements.whenDefined('popover-element');

            // Check if element has been upgraded (has _isOpen property from connectedCallback)
            if (this.popoverElement._isOpen === undefined) {
                // Wait a frame for connectedCallback to run
                await new Promise(resolve => requestAnimationFrame(resolve));
            }
        }

        async togglePopover() {
            if (this.isPopoverOpen()) {
                this.closePopover();
            } else {
                await this.openPopover();
            }
        }

        /**
         * Closes the popover. Works with both native and fallback modes.
         */
        closePopover() {
            if (!this.popoverElement) return;

            if (this.isUsingFallbackMode()) {
                // For popover-element, use the custom hidePopover method
                if (typeof this.popoverElement.hidePopover === 'function') {
                    this.popoverElement.hidePopover();
                } else {
                    // Fallback: manually hide using class
                    this.popoverElement.classList.remove('popover-open');
                    this.popoverElement.style.display = 'none';
                }
            } else {
                // Native popover
                this.popoverElement.hidePopover();
            }
        }

        async openPopover() {
            // Before opening, mark that this modal is opening
            // NOTE: We do NOT close other modals with prevent-close - they should remain open
            if (this.popoverElement && this.popoverElement.id) {
                // Use the same mechanism as dialog-popover to track opening modals
                if (window.DialogPopover && window.DialogPopover.openingModalId !== undefined) {
                    window.DialogPopover.openingModalId = this.popoverElement.id;
                }

                // Only close modals that don't have prevent-close enabled
                // Modals with prevent-close should remain open when opening another modal
                // Query both native popovers and fallback popover-elements
                const openDialogs = document.querySelectorAll('[popover]:popover-open, popover-element.popover-open');
                openDialogs.forEach(dialog => {
                    if (dialog !== this.popoverElement) {
                        const dialogComponent = dialog.closest('dialog-popover');

                        // Only close if prevent-close is NOT enabled
                        if (dialogComponent && !dialogComponent.shouldPreventClose) {
                            // Use the component's closeDialog method
                            dialogComponent.closeDialog();
                        } else if (!dialogComponent) {
                            // If no component, check if it has prevent-close attribute
                            const hasPreventClose = dialog.closest('[data-prevent-close="true"]') !== null;
                            if (!hasPreventClose) {
                                // Only close if prevent-close is not enabled
                                dialog.setAttribute('data-programmatic-close', 'true');
                                if (typeof dialog.hidePopover === 'function') {
                                    dialog.hidePopover();
                                }
                                Promise.resolve().then(() => {
                                    dialog.removeAttribute('data-programmatic-close');
                                });
                            }
                        }
                        // If prevent-close is enabled, do nothing - let it stay open
                    }
                });
            }

            // For popover-element, ensure it's ready before calling showPopover
            if (this.isUsingFallbackMode()) {
                await this.ensurePopoverElementReady();

                // Use the custom showPopover method
                if (typeof this.popoverElement.showPopover === 'function' &&
                    this.popoverElement._isOpen !== undefined) {
                    this.popoverElement.showPopover();
                } else {
                    // Fallback: manually show using class
                    this.popoverElement.classList.add('popover-initialized', 'popover-open');
                    this.popoverElement.style.display = '';
                }
            } else {
                // Native popover
                this.popoverElement.showPopover();
            }
        }
    }

    customElements.define('external-popover', ExternalPopover);
}